import { useId } from 'react'
import { cn } from '@/lib/cn'
import { normalizeHex, readableTextColor } from '@/lib/color'

/** Paleta de partida — cores da marca e as usuais de status. */
const PRESETS = [
  '#f5821f', '#0e2841', '#2563eb', '#16a34a',
  '#dc2626', '#f59e0b', '#7c3aed', '#6b7280',
]

interface Props {
  label?: string
  hint?: string
  error?: string
  value: string | null | undefined
  onChange: (hex: string) => void
  disabled?: boolean
  /** Texto de exemplo dentro da prévia (ex.: o nome da etiqueta). */
  previewText?: string
  className?: string
}

/**
 * Seletor de cor com paleta + cor livre.
 *
 * Substitui o select de nomes técnicos ("gray", "amber"), que obrigava o
 * usuário a adivinhar a cor pelo nome em inglês. O valor é sempre `#rrggbb`.
 */
export function ColorPicker({
  label, hint, error, value, onChange, disabled, previewText, className,
}: Props) {
  const id = useId()
  const current = normalizeHex(value) ?? PRESETS[0]

  return (
    <div className={cn('flex flex-col', className)}>
      {label && (
        <label htmlFor={id} className="mb-1.5 text-xs font-medium text-gray-700">
          {label}
        </label>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map((hex) => {
          const active = hex === current
          return (
            <button
              key={hex}
              type="button"
              disabled={disabled}
              onClick={() => onChange(hex)}
              aria-label={`Cor ${hex}`}
              title={hex}
              className={cn(
                'h-7 w-7 cursor-pointer rounded-full transition disabled:cursor-not-allowed disabled:opacity-50',
                active ? 'ring-2 ring-gray-800 ring-offset-2' : 'ring-1 ring-gray-200 hover:scale-110',
              )}
              style={{ backgroundColor: hex }}
            />
          )
        })}

        {/* Cor livre: o input nativo abre a paleta do sistema. */}
        <label
          className={cn(
            'inline-flex h-7 cursor-pointer items-center gap-1.5 rounded-md px-2 text-[11px] ring-1 ring-gray-200 transition hover:bg-gray-50',
            disabled && 'cursor-not-allowed opacity-50',
          )}
          title="Escolher outra cor"
        >
          <span className="h-4 w-4 rounded-full ring-1 ring-gray-300" style={{ backgroundColor: current }} />
          <span className="font-mono uppercase text-gray-600">{current}</span>
          <input
            id={id}
            type="color"
            className="sr-only"
            value={current}
            disabled={disabled}
            onChange={(e) => onChange(normalizeHex(e.target.value) ?? e.target.value)}
          />
        </label>
      </div>

      {previewText !== undefined && (
        <div className="mt-2.5">
          <span className="mr-2 text-[11px] text-gray-500">Prévia:</span>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium"
            style={{ backgroundColor: current, color: readableTextColor(current) }}
          >
            {previewText || 'Etiqueta'}
          </span>
        </div>
      )}

      {(error || hint) && (
        <p className={cn('mt-1 text-xs', error ? 'text-rose-600' : 'text-gray-500')}>
          {error || hint}
        </p>
      )}
    </div>
  )
}
